import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import { useToast } from '../../Toast';
import BibTeXEditor from '../../BibTeXEditor';
import ExportReferences from '../references/ExportReferences';
import SortableCategoryGroup from '../shared/SortableCategoryGroup';
import SortableReferenceCard from '../shared/SortableReferenceCard';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import lavenderIconLight from '../../assets/blue_unfilled_icon_light.png';
import lavenderIconDark from '../../assets/lavender_unfilled_icon_dark.png';

const API_URL = '/api';

/**
 * ProjectDetail component - Displays single project with references, collaborators, categories
 * Handles reference CRUD, bulk operations, drag-and-drop reordering, export
 */
function ProjectDetail({ projectId }) {
  const [references, setReferences] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [showAddRef, setShowAddRef] = useState(false);
  const [showAddCollab, setShowAddCollab] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [bibtexRaw, setBibtexRaw] = useState('');
  const [collabEmail, setCollabEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sortBy, setSortBy] = useState('display_order');
  const [sortOrder, setSortOrder] = useState('desc');
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [categoryObjects, setCategoryObjects] = useState([]); // Full category objects from API
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const { darkMode, toggleDarkMode} = React.useContext(AuthContext);
  const toast = useToast();

  // Bulk operations state
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionsExiting, setBulkActionsExiting] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [showBulkCategoryInput, setShowBulkCategoryInput] = useState(false);

  // Refs for auto-focusing category inputs
  const newCategoryInputRef = useRef(null);
  const bulkCategoryInputRef = useRef(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadProjectInfo();
    loadReferences();
    loadCollaborators();
    loadCategories();
  }, [projectId]);

  // Auto-focus new category input when it appears
  useEffect(() => {
    if (showNewCategoryInput && newCategoryInputRef.current) {
      newCategoryInputRef.current.focus();
    }
  }, [showNewCategoryInput]);

  // Auto-focus bulk category input when it appears
  useEffect(() => {
    if (showBulkCategoryInput && bulkCategoryInputRef.current) {
      bulkCategoryInputRef.current.focus();
    }
  }, [showBulkCategoryInput]);

  useEffect(() => {
    // Extract unique category names from references for backward compatibility
    const uniqueCategories = [...new Set(references.map(r => r.category).filter(c => c))];
    setCategories(uniqueCategories.sort());
  }, [references]);

  const loadProjectInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}`);
      setProjectInfo(response.data);
    } catch (err) {
      console.error('Failed to load project info', err);
    }
  };

  const loadReferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/references`);
      setReferences(response.data);
    } catch (err) {
      console.error('Failed to load references', err);
    }
  };

  const loadCollaborators = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/collaborators`);
      setCollaborators(response.data);
    } catch (err) {
      console.error('Failed to load collaborators', err);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/categories`);
      console.log('Loaded categories:', response.data);
      setCategoryObjects(response.data);
    } catch (err) {
      console.error('Failed to load categories:', err.response?.data || err.message);
      // Don't show toast error on initial load, just log it
    }
  };

  const addReference = async (e) => {
    e.preventDefault();

    // Handle new category creation
    if (showNewCategoryInput && newCategoryName.trim()) {
      setSelectedCategory(newCategoryName.trim());
      setShowNewCategoryInput(false);
      // Don't submit yet, let user see the selection
      toast.info(`Category "${newCategoryName.trim()}" selected. Click "Add Reference(s)" to submit.`, 3000);
      setNewCategoryName('');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/projects/${projectId}/references`, {
        bibtex_raw: bibtexRaw,
        category: selectedCategory
      });

      const data = response.data;

      // Build success message
      let message = data.message;

      // Show detailed success info
      if (data.added > 0) {
        if (data.partial) {
          // Partial success - some failed
          const successList = data.references.map(r => r.title || r.bibtex_key).join(', ');
          const failedList = data.errors.map(err => err.bibtex_key).join(', ');
          toast.warning(`${message}\n\nAdded: ${successList}\n\nFailed: ${failedList}`, 8000);
        } else {
          // Full success
          if (data.added === 1) {
            toast.success(`Successfully added: ${data.references[0].title || data.references[0].bibtex_key}`);
          } else {
            toast.success(`${message}`, 5000);
          }
        }
        setBibtexRaw('');
        setSelectedCategory('');
        setShowAddRef(false);
        loadReferences();
        loadCategories(); // Reload categories after adding reference
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errors) {
        // Show detailed error info
        const errorList = errorData.errors.map(e => `${e.bibtex_key}: ${e.error}`).join('\n');
        toast.error(`Failed to add references:\n${errorList}`, 8000);
      } else {
        toast.error('Failed to add reference: ' + (errorData?.error || 'Unknown error'));
      }
    }
  };

  const updateReference = async (refId, data, isBibtexUpdate = false) => {
    try {
      const response = await axios.put(`${API_URL}/references/${refId}`, data);

      // If BibTeX was updated, the response contains the full updated reference
      if (isBibtexUpdate && response.data.reference) {
        // Update the reference in the local state directly for immediate UI update
        setReferences(prevRefs =>
          prevRefs.map(ref => ref.id === refId ? response.data.reference : ref)
        );
        toast.success('BibTeX updated successfully');
      } else {
        // For annotation updates, reload all references
        loadReferences();
        toast.success('Reference updated successfully');
      }
      setEditingRef(null);
    } catch (err) {
      if (isBibtexUpdate) {
        // Re-throw for bibtex errors so the ReferenceCard can display them
        throw err;
      } else {
        toast.error('Failed to update reference');
      }
    }
  };

  const deleteReference = async (refId) => {
    // if (!window.confirm('Delete this reference?')) return;
    try {
      await axios.delete(`${API_URL}/references/${refId}`);
      toast.success('Reference deleted successfully');
      loadReferences();
    } catch (err) {
      toast.error('Failed to delete reference');
    }
  };

  const addCollaborator = async (e) => {
    e.preventDefault();
    setSendingInvite(true);
    try {
      const response = await axios.post(`${API_URL}/projects/${projectId}/collaborators`, {
        email: collabEmail
      });
      setCollabEmail('');
      setShowAddCollab(false);
      toast.success(response.data.message || 'Invitation sent successfully');
      loadCollaborators();
    } catch (err) {
      toast.error('Failed to send invitation: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setSendingInvite(false);
    }
  };

  const removeCollaborator = async (collabId, collabUsername) => {
    if (!window.confirm(`Remove ${collabUsername} from this project?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/projects/${projectId}/collaborators/${collabId}`);
      toast.success(`Collaborator "${collabUsername}" removed`);
      loadCollaborators();
    } catch (err) {
      toast.error('Failed to remove collaborator: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const copyBibtex = (bibtex) => {
    navigator.clipboard.writeText(bibtex).then(() => {
      toast.success('BibTeX copied to clipboard!', 2000);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const getSortedReferences = () => {
    const sorted = [...references].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'display_order':
          aVal = a.display_order || 0;
          bVal = b.display_order || 0;
          // Always ascending for custom order
          return aVal - bVal;
        case 'author':
          aVal = (a.author || '').toLowerCase();
          bVal = (b.author || '').toLowerCase();
          break;
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'rating':
          aVal = a.rating || 0;
          bVal = b.rating || 0;
          break;
        case 'year':
          aVal = parseInt(a.year) || 0;
          bVal = parseInt(b.year) || 0;
          break;
        case 'date':
        default:
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const getGroupedReferences = () => {
    const sorted = getSortedReferences();
    const grouped = {};

    sorted.forEach(ref => {
      const category = ref.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(ref);
    });

    return grouped;
  };

  // Get references grouped by category, ordered by category display_order
  const getOrderedGroupedReferences = () => {
    const sorted = getSortedReferences();

    // If we have category objects from API, use their order
    if (categoryObjects && categoryObjects.length > 0) {
      const orderedGroups = [];

      // First, add categories in their custom order
      categoryObjects.forEach(catObj => {
        const refs = sorted.filter(ref => ref.category === catObj.name);
        if (refs.length > 0) {
          orderedGroups.push({
            id: catObj.id,
            name: catObj.name,
            refs: refs
          });
        }
      });

      // Then add "Uncategorized" if it exists
      const uncategorized = sorted.filter(ref => !ref.category);
      if (uncategorized.length > 0) {
        orderedGroups.push({
          id: 'uncategorized',
          name: 'Uncategorized',
          refs: uncategorized
        });
      }

      return orderedGroups;
    }

    // Fallback to old behavior if no category objects
    const grouped = getGroupedReferences();
    return Object.entries(grouped).map(([name, refs]) => ({
      id: name,
      name: name,
      refs: refs
    }));
  };

  const toggleCategoryCollapse = (category) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Bulk operations functions
  const toggleSelectRef = (refId) => {
    const newSelected = new Set(selectedRefs);
    if (newSelected.has(refId)) {
      newSelected.delete(refId);
    } else {
      newSelected.add(refId);
    }
    setSelectedRefs(newSelected);
  };

  const selectAllRefs = () => {
    const allIds = new Set(references.map(r => r.id));
    setSelectedRefs(allIds);
  };

  const selectAllInCategory = (category) => {
    const categoryRefs = references.filter(r => (r.category || 'Uncategorized') === category);
    const newSelected = new Set(selectedRefs);
    categoryRefs.forEach(r => newSelected.add(r.id));
    setSelectedRefs(newSelected);
  };

  const deselectAll = () => {
    setSelectedRefs(new Set());
  };

  const toggleBulkActionsPanel = () => {
    if (showBulkActions) {
      // Start exit animation
      setBulkActionsExiting(true);
      // Wait for animation to complete before hiding
      setTimeout(() => {
        setShowBulkActions(false);
        setBulkActionsExiting(false);
      }, 300); // Match animation duration
    } else {
      setShowBulkActions(true);
    }
  };

  const bulkDelete = async () => {
    if (selectedRefs.size === 0) {
      toast.warning('No references selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedRefs.size} selected reference(s)? This cannot be undone.`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedRefs).map(refId =>
        axios.delete(`${API_URL}/references/${refId}`)
      );

      await Promise.all(deletePromises);

      toast.success(`Successfully deleted ${selectedRefs.size} reference(s)`);
      setSelectedRefs(new Set());
      loadReferences();
    } catch (err) {
      toast.error('Failed to delete some references: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const bulkChangeCategory = async () => {
    if (selectedRefs.size === 0) {
      toast.warning('No references selected');
      return;
    }

    if (!bulkCategory) {
      toast.warning('Please select a category');
      return;
    }

    try {
      const updatePromises = Array.from(selectedRefs).map(refId =>
        axios.put(`${API_URL}/references/${refId}`, { category: bulkCategory })
      );

      await Promise.all(updatePromises);

      toast.success(`Updated ${selectedRefs.size} reference(s) to category "${bulkCategory}"`);
      setBulkCategory('');
      setShowBulkCategoryInput(false);
      setSelectedRefs(new Set());
      loadReferences();
      loadCategories(); // Reload categories after bulk update
    } catch (err) {
      toast.error('Failed to update some references: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  // Drag and drop handlers
  const handleCategoryDragStart = () => {
    // Collapse all categories when starting to drag
    const allCategoryNames = getOrderedGroupedReferences().map(g => g.name);
    setCollapsedCategories(new Set(allCategoryNames));
  };

  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Skip if trying to reorder "Uncategorized"
    if (active.id === 'uncategorized' || over.id === 'uncategorized') {
      toast.warning('Cannot reorder the Uncategorized category');
      return;
    }

    console.log('Category drag - categoryObjects:', categoryObjects);
    console.log('Category drag - active.id:', active.id, 'over.id:', over.id);

    // Get the current ordered groups
    const currentGroups = getOrderedGroupedReferences();

    // If categoryObjects is empty but we have groups (using names as fallback)
    if ((!categoryObjects || categoryObjects.length === 0) && currentGroups.length > 0) {
      // Check if these are string IDs (category names)
      if (typeof active.id === 'string' && active.id !== 'uncategorized') {
        console.warn('Categories not loaded from database yet. Category order will not persist.');
        toast.warning('Categories are not fully loaded. Order changes will not be saved. Please add references with categories first.');
        return;
      }
    }

    // Check if we have category objects loaded from database
    if (!categoryObjects || categoryObjects.length === 0) {
      console.error('No category objects available for reordering');
      toast.error('No categories in database. Add references with categories first.');
      return;
    }

    const oldIndex = categoryObjects.findIndex(c => c.id === active.id);
    const newIndex = categoryObjects.findIndex(c => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.error('Category not found in categoryObjects', { active: active.id, over: over.id, categoryObjects });
      toast.error('Category not found. Please refresh the page.');
      return;
    }

    const newCategoryOrder = arrayMove(categoryObjects, oldIndex, newIndex);

    // Optimistic update
    setCategoryObjects(newCategoryOrder);

    // Persist to backend
    try {
      const reorderData = newCategoryOrder
        .filter(cat => typeof cat.id === 'number') // Only include real categories with numeric IDs
        .map((cat, index) => ({
          id: cat.id,
          position: index
        }));

      if (reorderData.length === 0) {
        console.error('No valid categories to reorder');
        toast.error('No categories to reorder');
        return;
      }

      await axios.put(`${API_URL}/projects/${projectId}/categories/reorder`, {
        categories: reorderData
      });

      toast.success('Category order updated');
    } catch (err) {
      // Rollback on error
      setCategoryObjects(categoryObjects);
      console.error('Category reorder error:', err.response?.data || err.message);
      toast.error('Failed to reorder categories: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReferenceDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = references.findIndex(r => r.id === active.id);
    const newIndex = references.findIndex(r => r.id === over.id);

    const newReferenceOrder = arrayMove(references, oldIndex, newIndex);

    // Update display_order field on each reference for optimistic update
    const updatedReferences = newReferenceOrder.map((ref, index) => ({
      ...ref,
      display_order: index
    }));

    // Optimistic update
    const oldReferences = references;
    setReferences(updatedReferences);

    // Persist to backend
    try {
      const reorderData = updatedReferences.map((ref, index) => ({
        id: ref.id,
        position: index
      }));

      await axios.put(`${API_URL}/projects/${projectId}/references/reorder`, {
        references: reorderData
      });

      toast.success('Reference order updated');
    } catch (err) {
      // Rollback on error
      setReferences(oldReferences);
      toast.error('Failed to reorder references');
    }
  };

  return (
    <div className="container">
      <div className="header">
        <Link to="/" className="back-link">← Back to Projects</Link>
        <h2>{projectInfo ? projectInfo.name : 'Loading...'}</h2>
        <div className="header-actions">
          <button onClick={toggleDarkMode} className="theme-toggle" title="Toggle dark mode">
            {darkMode ? '☀️' : '🕶️'}
          </button>
          <Link to="/account" className="account-btn" title="Account Settings">
            👤
          </Link>
        </div>
      </div>

      <div className="actions-bar">
        <button onClick={() => setShowAddRef(!showAddRef)}>
          {showAddRef ? 'Cancel' : 'Add Reference(s)'}
        </button>
        <button onClick={() => setShowAddCollab(!showAddCollab)}>
          {showAddCollab ? 'Cancel' : 'Add Collaborator'}
        </button>
        <button onClick={() => setShowExport(!showExport)} className="export-btn">
          {showExport ? 'Cancel Export' : 'Export References'}
        </button>
      </div>

      {showAddRef && (
        <form onSubmit={addReference} className="create-form">
          <div className="form-group">
            <label>Category</label>
            <div className="category-input-group">
              {!showNewCategoryInput ? (
                <>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowNewCategoryInput(true);
                        setSelectedCategory('');
                      } else {
                        setSelectedCategory(e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__new__">+ Create New Category</option>
                  </select>
                </>
              ) : (
                <>
                  <input
                    ref={newCategoryInputRef}
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Enter new category name"
                  />
                  <button 
                    type="button"
                    onClick={() => {
                      if (newCategoryName.trim()) {
                        setSelectedCategory(newCategoryName.trim());
                        setNewCategoryName('');
                        setShowNewCategoryInput(false);
                      }
                    }}
                    className="add-category-btn"
                  >
                    Add
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategoryName('');
                    }}
                    className="cancel-new-category"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
            {selectedCategory && !showNewCategoryInput && (
              <p className="selected-category-display">Selected: <strong>{selectedCategory}</strong></p>
            )}
          </div>
          <BibTeXEditor
            placeholder="Paste BibTeX entry/entries here (multiple references supported)..."
            value={bibtexRaw}
            onChange={(e) => setBibtexRaw(e.target.value)}
            height="250px"
            darkMode={darkMode}
          />
          <button type="submit">Add Reference(s)</button>
        </form>
      )}

      {showAddCollab && (
        <form onSubmit={addCollaborator} className="create-form">
          <input
            type="email"
            placeholder="Collaborator Email"
            value={collabEmail}
            onChange={(e) => setCollabEmail(e.target.value)}
            required
            disabled={sendingInvite}
          />
          <button type="submit" disabled={sendingInvite}>
            {sendingInvite ? 'Sending Invite...' : 'Send Invitation'}
          </button>
        </form>
      )}

      {showExport && (
        <ExportReferences
          references={groupByCategory ? getOrderedGroupedReferences().flatMap(g => g.refs) : getSortedReferences()}
          onClose={() => setShowExport(false)}
          projectName={projectInfo?.name || 'references'}
        />
      )}

      {collaborators.length > 0 && (
        <div className="collaborators-section">
          <h3>Collaborators</h3>
          <div className="collaborators-list">
            {collaborators.map(c => (
              <span key={c.id} className="collaborator-tag">
                {c.username}
                <button 
                  className="remove-collab-btn"
                  onClick={() => removeCollaborator(c.id, c.username)}
                  title="Remove collaborator"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="sort-controls">
        <div className="sort-buttons">
          <span>Sort by:</span>
          <button
            className={sortBy === 'display_order' ? 'active' : ''}
            onClick={() => setSortBy('display_order')}
          >
            Custom Order
          </button>
          <button
            className={sortBy === 'date' ? 'active' : ''}
            onClick={() => toggleSort('date')}
          >
            Date Added {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className={sortBy === 'author' ? 'active' : ''}
            onClick={() => toggleSort('author')}
          >
            Author {sortBy === 'author' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className={sortBy === 'title' ? 'active' : ''}
            onClick={() => toggleSort('title')}
          >
            Title {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className={sortBy === 'year' ? 'active' : ''}
            onClick={() => toggleSort('year')}
          >
            Year {sortBy === 'year' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className={sortBy === 'rating' ? 'active' : ''}
            onClick={() => toggleSort('rating')}
          >
            Rating {sortBy === 'rating' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>
        <div className="sort-actions">
          <label className="group-toggle">
            <input
              type="checkbox"
              checked={groupByCategory}
              onChange={(e) => setGroupByCategory(e.target.checked)}
            />
            Group by Category
          </label>
          <div className="sort-divider"></div>
          {selectedRefs.size > 0 ? (
            <>
              <span className="bulk-selection-count">
                <strong>{selectedRefs.size}</strong> selected
              </span>
              <button onClick={deselectAll} className="bulk-action-btn">
                Deselect All
              </button>
              <button
                onClick={toggleBulkActionsPanel}
                className="bulk-action-btn primary"
              >
                {showBulkActions ? 'Hide' : 'Show'} Actions
              </button>
            </>
          ) : (
            <button onClick={selectAllRefs} className="bulk-action-btn">
              Select All
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions panel */}
      {selectedRefs.size > 0 && showBulkActions && (
        <div className={`bulk-actions-panel ${bulkActionsExiting ? 'exiting' : ''}`}>
          <div className="bulk-action-section">
            <h4>Bulk Category Change</h4>
            <div className="bulk-category-controls">
              {!showBulkCategoryInput ? (
                <>
                  <select
                    value={bulkCategory}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowBulkCategoryInput(true);
                        setBulkCategory('');
                      } else {
                        setBulkCategory(e.target.value);
                      }
                    }}
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__new__">+ New Category</option>
                  </select>
                  <button
                    onClick={bulkChangeCategory}
                    disabled={!bulkCategory}
                    className="bulk-apply-btn"
                  >
                    Apply to {selectedRefs.size} reference(s)
                  </button>
                </>
              ) : (
                <>
                  <input
                    ref={bulkCategoryInputRef}
                    type="text"
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    placeholder="Enter new category name"
                  />
                  <button
                    onClick={() => {
                      if (bulkCategory.trim()) {
                        bulkChangeCategory();
                      }
                    }}
                    className="bulk-apply-btn"
                    disabled={!bulkCategory.trim()}
                  >
                    Apply to {selectedRefs.size} reference(s)
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkCategoryInput(false);
                      setBulkCategory('');
                    }}
                    className="bulk-cancel-btn"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bulk-action-section bulk-danger-zone">
            <h4>Danger Zone</h4>
            <button onClick={bulkDelete} className="bulk-delete-btn">
              Delete {selectedRefs.size} reference(s)
            </button>
          </div>
        </div>
      )}

      {groupByCategory ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleCategoryDragStart}
          onDragEnd={handleCategoryDragEnd}
        >
          <SortableContext
            items={getOrderedGroupedReferences().map(g => g.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="references-grouped">
              {getOrderedGroupedReferences().map((categoryGroup) => {
                const isCollapsed = collapsedCategories.has(categoryGroup.name);
                return (
                  <SortableCategoryGroup
                    key={categoryGroup.id}
                    categoryGroup={categoryGroup}
                    dragHandleProps={categoryGroup.id !== 'uncategorized'}
                  >
                    {(dragHandleProps) => (
                      <>
                        <div className="category-header-container">
                          <h3
                            className="category-header"
                            onClick={() => toggleCategoryCollapse(categoryGroup.name)}
                            style={{ cursor: 'pointer' }}
                          >
                            {dragHandleProps && (
                              <span className="drag-handle" {...dragHandleProps} onClick={(e) => e.stopPropagation()}>
                                ⋮⋮
                              </span>
                            )}
                            <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                              {isCollapsed ? '▶' : '▼'}
                            </span>
                            {categoryGroup.name}
                            <span className="category-count">({categoryGroup.refs.length})</span>
                          </h3>
                          <button
                            className="select-category-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInCategory(categoryGroup.name);
                            }}
                            title="Select all in this category"
                          >
                            Select All
                          </button>
                        </div>
                        {!isCollapsed && (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleReferenceDragEnd}
                          >
                            <SortableContext
                              items={categoryGroup.refs.map(r => r.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="references-list">
                                {categoryGroup.refs.map(reference => (
                                  <SortableReferenceCard
                                    key={reference.id}
                                    reference={reference}
                                    editingRef={editingRef}
                                    setEditingRef={setEditingRef}
                                    updateReference={updateReference}
                                    deleteReference={deleteReference}
                                    copyBibtex={copyBibtex}
                                    categories={categories}
                                    hideCategory={groupByCategory}
                                    isSelected={selectedRefs.has(reference.id)}
                                    onToggleSelect={toggleSelectRef}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </>
                    )}
                  </SortableCategoryGroup>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleReferenceDragEnd}
        >
          <SortableContext
            items={getSortedReferences().map(r => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="references-list">
              {getSortedReferences().map(reference => (
                <SortableReferenceCard
                  key={reference.id}
                  reference={reference}
                  editingRef={editingRef}
                  setEditingRef={setEditingRef}
                  updateReference={updateReference}
                  deleteReference={deleteReference}
                  copyBibtex={copyBibtex}
                  categories={categories}
                  hideCategory={false}
                  isSelected={selectedRefs.has(reference.id)}
                  onToggleSelect={toggleSelectRef}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// Sortable wrapper for Category Groups

export default ProjectDetail;
